import { Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	SovosButton,
	SovosDialog,
	SovosDialogActions,
	SovosDialogContent,
	SovosDialogTitle,
	SovosPageContentTable,
	SovosSnackbar,
} from 'mosaic-react';
import React, { useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import {
	deleteLocationMapping,
	fetchLocationMappings,
} from 'src/api/locationMappings';
import EmptyPage from 'src/components/emptyPage/EmptyPage';
import type { Dialog } from 'src/interfaces/Dialog';
import type { LocationMapping } from 'src/interfaces/LocationMapping';
import type { Pagination } from 'src/interfaces/Pagination';

import {
	DEFAULT_CURRENT_PAGE,
	DEFAULT_PAGE_SIZE,
	DIALOG_CONTENT_MULTIPLE_ROW,
	DIALOG_CONTENT_SINGLE_ROW,
	DIALOG_TITLE_SINGLE_ROW,
	TOOLTIP_MULTIPLE_ROWS,
	TOOLTIP_SINGLE_ROW,
	initialColumns as columns,
} from './constant/constant';

const dialogDefaultValue: Dialog = { isOpen: false, title: '', content: '' };

const LocationsMappingTable: React.FC = () => {
	const queryClient = useQueryClient();

	const location = useLocation();
	const navigationType = useNavigationType();

	const [locationMappingSavedSnackbar, setLocationMappingSavedSnackbar] =
		useState(true);
	const [dialog, setDialog] = useState<Dialog>(dialogDefaultValue);
	const [locationMappingsToDelete, setLocationMappingsToDelete] = useState<
		number[]
	>([]);
	const [pagination, setPagination] = useState<Pagination>({
		page: DEFAULT_CURRENT_PAGE,
		perPage:
			Number(sessionStorage.getItem('locationMappingPerPage')) ||
			DEFAULT_PAGE_SIZE,
	});
	const [selectedRows, setSelectedRows] = useState<LocationMapping[]>([]);
	const navigate = useNavigate();

	const fetchLocMapQueryKey = ['LocationMappings', pagination];
	const fetchLocMapQuery = useQuery(
		fetchLocMapQueryKey,
		() => fetchLocationMappings(pagination),
		{
			refetchInterval: 60 * 1000,
		}
	);

	const deleteLocMapMutationQuery = useMutation(deleteLocationMapping, {
		onSuccess: () => {
			fetchLocMapQuery.refetch();
		},

		// add data to cache as soon as mutation starts
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: fetchLocMapQueryKey });

			const previous = queryClient.getQueryData(fetchLocMapQueryKey);

			queryClient.setQueryData<
				Awaited<ReturnType<typeof fetchLocationMappings>>
			>(fetchLocMapQueryKey, (cachedData) =>
				cachedData
					? {
							pageState: {
								...cachedData.pageState,
								totalEntries:
									cachedData.pageState.totalEntries -
									locationMappingsToDelete.length,
							},
							items: cachedData.items.filter(
								(item) => !locationMappingsToDelete.includes(item.id)
							),
					  }
					: cachedData
			);

			setSelectedRows([]);
			setLocationMappingsToDelete([]);
			setDialog(dialogDefaultValue);

			return { previous };
		},
		onError: (err, data, context) => {
			// restore previous cache if error
			if (context?.previous) {
				queryClient.setQueryData(fetchLocMapQueryKey, context?.previous);
			}
		},
		onSettled: () => {
			// refetch rate files after mutation
			queryClient.invalidateQueries({ queryKey: fetchLocMapQueryKey });
		},
	});

	const onPageChange = async (newPage: number, newItemsPerPage: number) => {
		sessionStorage.setItem(
			'locationMappingPerPage',
			newItemsPerPage.toString()
		);
		setPagination({
			page: newPage - 1,
			perPage: newItemsPerPage,
		});
		fetchLocMapQuery.refetch();
	};

	const onCloseDialog = (): void => {
		setLocationMappingsToDelete([]);
		setDialog(dialogDefaultValue);
	};

	const isRowSelected = (row: LocationMapping): boolean => {
		return !!selectedRows.find((r) => r.id === row.id);
	};

	const toggleRowSelection = (row: LocationMapping): void => {
		const selectedRow = selectedRows.find((r) => r.id === row.id);
		if (selectedRows.length > 0) {
			if (selectedRow) {
				setSelectedRows(selectedRows.filter((r) => r.id !== selectedRow.id));
			} else {
				setSelectedRows([...selectedRows, row]);
			}
		} else {
			setSelectedRows([row]);
		}
	};

	const areAllRowsSelected = () => {
		return (
			fetchLocMapQuery.data &&
			fetchLocMapQuery.data.items.length === selectedRows.length
		);
	};

	const areAnyRowsSelected = (): boolean => selectedRows.length > 0;

	const toggleSelectAll = (): void => {
		if (selectedRows.length === fetchLocMapQuery.data?.items.length) {
			setSelectedRows([]);
		} else if (fetchLocMapQuery.data?.items) {
			setSelectedRows(fetchLocMapQuery.data.items);
		}
	};

	return (
		<>
			{fetchLocMapQuery?.data?.items.length === 0 &&
			!fetchLocMapQuery?.isLoading ? (
				<EmptyPage
					title='Create a Location Template'
					subtitle='To get the latest tax rates, create a location template'
					titleButton='Create'
				/>
			) : (
				<SovosPageContentTable
					isLoading={fetchLocMapQuery.isLoading}
					PaginationFooterProps={{
						itemsPerPage: pagination.perPage,
						currentPage: pagination.page + 1,
						totalItems: fetchLocMapQuery.data?.pageState.totalEntries,
						totalPages: fetchLocMapQuery.data?.pageState.totalPages,
						onPageChange,
					}}
					onRowSelection={{
						isRowSelected,
						toggleRowSelection,
						areAnyRowsSelected,
						areAllRowsSelected,
						toggleSelectAll,
						selectedCount: selectedRows.length,
					}}
					bulkActions={[
						{
							icon: <Delete />,
							onClick: () => {
								setLocationMappingsToDelete(selectedRows.map((row) => row.id));
								const title =
									selectedRows?.length > 1
										? `You've selected ${selectedRows.length} location mappings to delete`
										: DIALOG_TITLE_SINGLE_ROW;
								const content =
									selectedRows.length > 1
										? DIALOG_CONTENT_MULTIPLE_ROW
										: DIALOG_CONTENT_SINGLE_ROW;
								setDialog({ isOpen: true, title, content });
							},
							tooltipText:
								selectedRows.length > 1
									? TOOLTIP_MULTIPLE_ROWS
									: TOOLTIP_SINGLE_ROW,
						},
					]}
					TableProps={{
						columns,
						data: fetchLocMapQuery.data?.items || [],
						fixedHeader: true,
						rowActions: {
							icon: <Delete />,
							onClick: (row: LocationMapping) => {
								setLocationMappingsToDelete([row.id]);
								setDialog({
									isOpen: true,
									title: DIALOG_TITLE_SINGLE_ROW,
									content: DIALOG_CONTENT_SINGLE_ROW,
								});
							},
							tooltipText: TOOLTIP_SINGLE_ROW,
						},
						onRowClick: (row: LocationMapping) =>
							navigate(`/location-mappings/${row.id}`),
					}}
				/>
			)}

			<SovosSnackbar
				open={Boolean(
					locationMappingSavedSnackbar &&
						navigationType === 'PUSH' &&
						location.state?.savedLocationMapping
				)}
				onClose={() => setLocationMappingSavedSnackbar(false)}
				variant='confirmation'
				message={
					location.state?.savedLocationMapping === 'CREATE'
						? 'New location mapping created.'
						: 'Changes saved successfully.'
				}
				timeout='short'
			/>

			<SovosDialog open={dialog.isOpen} onClose={() => onCloseDialog()}>
				<SovosDialogTitle showCloseButton>{dialog.title}</SovosDialogTitle>
				<SovosDialogContent>{dialog.content}</SovosDialogContent>
				<SovosDialogActions>
					<SovosButton variant='text' onClick={() => onCloseDialog()}>
						Cancel
					</SovosButton>
					<SovosButton
						color='error'
						onClick={() => {
							deleteLocMapMutationQuery.mutate(locationMappingsToDelete);
						}}
					>
						Delete
					</SovosButton>
				</SovosDialogActions>
			</SovosDialog>
		</>
	);
};

export default LocationsMappingTable;
